import { useState } from "react";
import {
  ArrowDownRight,
  ArrowRight,
  Check,
  ChevronDown,
  MessageCircle,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const faqCategories = [
  {
    title: "General",
    subtitle: "About our services",
    faqs: [
      {
        question: "What services does Cube4Spaces & Paint Studio offer?",
        answer:
          "We provide interior design, construction, complete project solutions, interior painting, exterior painting, waterproofing, and selected decorative finishes. You can choose an individual service or discuss a coordinated solution for your project.",
      },
      {
        question: "Do you work on both homes and commercial properties?",
        answer:
          "Yes. Our services can be tailored to residential spaces such as apartments, villas, and independent houses, as well as commercial spaces such as offices, retail stores, and other business environments.",
      },
      {
        question: "Where do you provide your services?",
        answer:
          "Contact our team with your project location and requirements. We can discuss the scope of work and confirm whether the location is within our service area.",
      },
    ],
  },
  {
    title: "Construction",
    subtitle: "Planning and execution",
    faqs: [
      {
        question: "Can I hire you for construction alone?",
        answer:
          "Yes. You can discuss construction as a standalone requirement or combine it with interior design and finishing services. The final scope will be agreed upon before work begins.",
      },
      {
        question: "How do you plan a construction project?",
        answer:
          "We begin by understanding your requirements, site conditions, budget, and expected timeline. The proposed scope, material specifications, estimated costs, and execution stages can then be reviewed with you.",
      },
      {
        question: "How do you monitor quality during construction?",
        answer:
          "Quality management involves appropriate material selection, coordination of work, site inspections, and reviewing important stages of execution. The checks required depend on the project's scope and specifications.",
      },
    ],
  },
  {
    title: "Interior Design",
    subtitle: "Design and functionality",
    faqs: [
      {
        question: "Can I review the design before execution?",
        answer:
          "Design planning and visual references can help you understand the proposed space before work begins. The deliverables, such as layouts and 3D visualisations, will be confirmed according to your selected project scope.",
      },
      {
        question: "Which interior elements can you help design?",
        answer:
          "Depending on the project, interior design may include space planning, modular kitchens, wardrobes, ceiling treatments, lighting, furniture, storage solutions, colour palettes, and finish selection.",
      },
      {
        question: "Can you design interiors for a small home?",
        answer:
          "Yes. Smaller spaces can benefit from thoughtful layouts, efficient storage, multifunctional furniture, and well-planned lighting. Recommendations are tailored to the available area and your everyday needs.",
      },
    ],
  },
  {
    title: "Painting",
    subtitle: "Interior and exterior finishes",
    faqs: [
      {
        question: "Do you offer interior and exterior painting?",
        answer:
          "Yes. You can enquire about interior wall painting, exterior painting, colour selection, surface preparation, and suitable paint finishes for your property.",
      },
      {
        question: "How do I choose the right paint for my home?",
        answer:
          "Consider the surface condition, room usage, lighting, cleaning requirements, exposure to weather, and desired finish. Our team can help you evaluate suitable options for the areas being painted.",
      },
      {
        question: "Can you help with damp walls and waterproofing?",
        answer:
          "Waterproofing and paint selection should start with understanding the source of moisture and the surface condition. We can discuss the affected area and the appropriate scope of inspection and treatment.",
      },
    ],
  },
  {
    title: "Budget & Timeline",
    subtitle: "Costs and scheduling",
    faqs: [
      {
        question: "How much will my project cost?",
        answer:
          "Costs depend on the area, design complexity, materials, finishes, site conditions, and the scope of work. Share your requirements so an estimate can be prepared around the agreed specifications.",
      },
      {
        question: "How long will my project take?",
        answer:
          "The timeline depends on the size of the project, material availability, site readiness, and work involved. A proposed schedule should be discussed after the scope has been assessed.",
      },
      {
        question: "Will I receive a breakdown of the work?",
        answer:
          "Before proceeding, ask for a clear written scope that identifies the agreed work, materials, estimated costs, payment milestones, exclusions, and expected timeline.",
      },
    ],
  },
  {
    title: "Materials & Process",
    subtitle: "Choices and coordination",
    faqs: [
      {
        question: "Can I select my preferred materials and finishes?",
        answer:
          "Yes. You can discuss your preferences, budget, and practical requirements with the team. Final selections should be documented and approved before the relevant work begins.",
      },
      {
        question: "How do I get started with a project?",
        answer:
          "Start by sharing your project type, location, approximate area, requirements, budget range, and preferred timeline. The next steps can then be discussed based on your needs.",
      },
      {
        question: "Can I combine interiors and painting in one project?",
        answer:
          "Yes. Interior design and painting requirements can be discussed together to coordinate colours, finishes, work sequencing, and the overall appearance of your space.",
      },
    ],
  },
];

export default function FAQ() {
  const [activeCategory, setActiveCategory] = useState(0);
  const [openIndex, setOpenIndex] = useState(0);

  const currentCategory = faqCategories[activeCategory];

  const handleCategoryChange = (index) => {
    setActiveCategory(index);
    setOpenIndex(0);
  };

  const toggleFAQ = (index) => {
    setOpenIndex((current) => (current === index ? null : index));
  };

  const handleContact = () => {
    const contactSection = document.getElementById("contact");

    if (contactSection) {
      contactSection.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    } else {
      window.location.href = "/contact";
    }
  };

  return (
    <section
      id="faq"
      className="relative isolate w-full overflow-hidden bg-[#f7f8fc] py-14 sm:py-16 md:py-20 lg:py-24"
    >
      {/* Background decoration */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 top-0 -z-10 h-64 w-64 rounded-full bg-indigo-100/70 blur-[100px] sm:h-80 sm:w-80 lg:h-[420px] lg:w-[420px]"
      />

      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-24 -left-24 -z-10 h-72 w-72 rounded-full bg-slate-200/70 blur-[100px] sm:h-96 sm:w-96"
      />

      <div className="mx-auto w-full max-w-[1400px] px-4 sm:px-6 md:px-8 lg:px-10 xl:px-14 2xl:px-16">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.55 }}
          className="mb-10 grid gap-6 sm:mb-12 md:gap-8 lg:mb-14 lg:grid-cols-[1fr_0.75fr] lg:items-end"
        >
          <div className="min-w-0">
            <div className="mb-4 inline-flex items-center gap-2.5 rounded-full border border-indigo-100 bg-white px-3.5 py-2 shadow-sm sm:mb-5 sm:px-4">
              <span className="h-2 w-2 rounded-full bg-indigo-600" />

              <span className="font-['DM_Sans',sans-serif] text-[10px] font-bold uppercase tracking-[0.18em] text-indigo-700 sm:text-xs">
                Frequently Asked Questions
              </span>
            </div>

            <h2 className="max-w-3xl font-['Poppins',sans-serif] text-3xl font-semibold leading-[1.2] tracking-tight text-slate-950 sm:text-4xl md:text-5xl lg:text-[3.5rem] xl:text-6xl">
              Got Questions?
              <span className="mt-1 block text-indigo-600">
                Let's Make It Clear.
              </span>
            </h2>
          </div>

          <div className="min-w-0 lg:max-w-md lg:justify-self-end">
            <p className="font-['DM_Sans',sans-serif] text-sm leading-7 text-slate-600 sm:text-base sm:leading-8">
              Find answers about our design, construction, painting, materials,
              project planning, and costs. Choose a topic to explore the
              information relevant to your project.
            </p>

            <div className="mt-5 flex flex-wrap gap-2 sm:mt-6">
              {["Design", "Construction", "Painting"].map((item) => (
                <span
                  key={item}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1.5 font-['DM_Sans',sans-serif] text-xs font-semibold text-slate-600 sm:px-4 sm:py-2 sm:text-sm"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        </motion.div>

        {/* FAQ layout */}
        <div className="grid min-w-0 grid-cols-1 items-start gap-6 md:gap-8 lg:grid-cols-[280px_minmax(0,1fr)] xl:grid-cols-[320px_minmax(0,1fr)] xl:gap-12">
          {/* Category navigation */}
          <aside className="min-w-0 lg:sticky lg:top-24">
            <div className="mb-3 flex items-center justify-between sm:mb-4">
              <h3 className="font-['Poppins',sans-serif] text-sm font-semibold text-slate-900 sm:text-base">
                Browse by topic
              </h3>

              <span className="font-['DM_Sans',sans-serif] text-xs text-slate-500">
                {faqCategories.length} topics
              </span>
            </div>

            <div className="flex gap-2 overflow-x-auto pb-3 [scrollbar-width:thin] lg:flex-col lg:overflow-visible lg:pb-0">
              {faqCategories.map((category, index) => {
                const isActive = activeCategory === index;

                return (
                  <button
                    key={category.title}
                    type="button"
                    onClick={() => handleCategoryChange(index)}
                    aria-pressed={isActive}
                    className={`group flex min-h-12 shrink-0 items-center justify-between gap-4 rounded-xl border px-4 py-3 text-left transition-all duration-200 sm:min-h-[58px] sm:px-5 lg:w-full ${
                      isActive
                        ? "border-slate-900 bg-slate-900 text-white shadow-lg shadow-slate-900/10"
                        : "border-slate-200 bg-white text-slate-600 hover:border-indigo-200 hover:bg-indigo-50/60 hover:text-indigo-700"
                    }`}
                  >
                    <span className="min-w-0">
                      <span className="block whitespace-nowrap font-['Poppins',sans-serif] text-xs font-semibold sm:text-sm">
                        {category.title}
                      </span>

                      <span
                        className={`mt-0.5 hidden font-['DM_Sans',sans-serif] text-xs lg:block ${
                          isActive ? "text-slate-300" : "text-slate-400"
                        }`}
                      >
                        {category.subtitle}
                      </span>
                    </span>

                    <span
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors ${
                        isActive
                          ? "bg-white/10 text-white"
                          : "bg-slate-50 text-slate-400 group-hover:text-indigo-600"
                      }`}
                    >
                      <ArrowRight
                        className={`h-3.5 w-3.5 transition-transform ${
                          isActive ? "translate-x-0.5" : ""
                        }`}
                      />
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Help card */}
            <div className="mt-5 hidden rounded-2xl border border-slate-200 bg-white p-5 lg:block xl:mt-7 xl:p-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                <MessageCircle className="h-5 w-5" />
              </div>

              <h4 className="mt-4 font-['Poppins',sans-serif] text-base font-semibold text-slate-900">
                Need more details?
              </h4>

              <p className="mt-2 font-['DM_Sans',sans-serif] text-sm leading-6 text-slate-500">
                Tell us about your space and we can discuss the next steps.
              </p>

              <button
                type="button"
                onClick={handleContact}
                className="mt-4 inline-flex items-center gap-2 font-['DM_Sans',sans-serif] text-sm font-bold text-indigo-700 hover:text-indigo-900"
              >
                Contact our team
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </aside>

          {/* Questions panel */}
          <div className="min-w-0">
            <div className="mb-5 flex flex-col gap-3 border-b border-slate-200 pb-5 sm:mb-6 sm:flex-row sm:items-end sm:justify-between sm:pb-6">
              <div>
                <p className="font-['DM_Sans',sans-serif] text-[10px] font-bold uppercase tracking-[0.17em] text-indigo-600 sm:text-xs">
                  Your Questions Answered
                </p>

                <h3 className="mt-2 font-['Poppins',sans-serif] text-xl font-semibold tracking-tight text-slate-950 sm:text-2xl md:text-3xl">
                  {currentCategory.title}
                </h3>
              </div>

              <span className="inline-flex w-fit items-center rounded-full border border-slate-200 bg-white px-3 py-1.5 font-['DM_Sans',sans-serif] text-xs font-semibold text-slate-500">
                {currentCategory.faqs.length} questions
              </span>
            </div>

            <div className="space-y-3 sm:space-y-4">
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={activeCategory}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.22 }}
                  className="space-y-3 sm:space-y-4"
                >
                  {currentCategory.faqs.map((faq, index) => {
                    const isOpen = openIndex === index;
                    const answerId = `faq-answer-${activeCategory}-${index}`;
                    const questionId = `faq-question-${activeCategory}-${index}`;

                    return (
                      <article
                        key={faq.question}
                        className={`overflow-hidden rounded-2xl border transition-colors duration-200 sm:rounded-3xl ${
                          isOpen
                            ? "border-indigo-200 bg-white shadow-[0_12px_32px_rgba(15,23,42,0.055)]"
                            : "border-slate-200/80 bg-white hover:border-slate-300"
                        }`}
                      >
                        <button
                          id={questionId}
                          type="button"
                          aria-expanded={isOpen}
                          aria-controls={answerId}
                          onClick={() => toggleFAQ(index)}
                          className="flex min-h-[76px] w-full items-center gap-3 px-4 py-4 text-left sm:min-h-[88px] sm:gap-5 sm:px-6 sm:py-5 md:px-7"
                        >
                          <span
                            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl font-['DM_Sans',sans-serif] text-xs font-bold transition-colors sm:h-10 sm:w-10 sm:text-sm ${
                              isOpen
                                ? "bg-indigo-600 text-white"
                                : "bg-slate-100 text-slate-500"
                            }`}
                          >
                            {String(index + 1).padStart(2, "0")}
                          </span>

                          <span className="min-w-0 flex-1 font-['Poppins',sans-serif] text-sm font-medium leading-6 text-slate-900 sm:text-base md:text-[17px] md:leading-7">
                            {faq.question}
                          </span>

                          <span
                            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-all duration-200 sm:h-9 sm:w-9 ${
                              isOpen
                                ? "rotate-180 bg-indigo-50 text-indigo-600"
                                : "bg-slate-50 text-slate-500"
                            }`}
                          >
                            <ChevronDown className="h-4 w-4 sm:h-[18px] sm:w-[18px]" />
                          </span>
                        </button>

                        <div
                          id={answerId}
                          role="region"
                          aria-labelledby={questionId}
                          hidden={!isOpen}
                        >
                          <div className="px-4 pb-5 pl-[68px] sm:px-6 sm:pb-6 sm:pl-[84px] md:px-7 md:pl-[92px]">
                            <div className="border-t border-slate-100 pt-4 sm:pt-5">
                              <p className="font-['DM_Sans',sans-serif] text-sm leading-7 text-slate-600 sm:text-[15px] sm:leading-8">
                                {faq.answer}
                              </p>

                              <div className="mt-4 flex items-start gap-2.5">
                                <Check className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" />

                                <span className="font-['DM_Sans',sans-serif] text-xs leading-5 text-slate-500 sm:text-sm">
                                  Every project is discussed according to its
                                  specific requirements.
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Contact CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.55 }}
          className="relative mt-12 overflow-hidden rounded-2xl bg-slate-950 p-6 sm:mt-14 sm:rounded-3xl sm:p-8 md:p-10 lg:mt-16 lg:p-12"
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-10 -top-24 h-56 w-56 rounded-full border border-white/10 sm:h-72 sm:w-72"
          />

          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-2 -top-16 h-44 w-44 rounded-full border border-white/10 sm:h-56 sm:w-56"
          />

          <div className="relative flex flex-col gap-6 md:flex-row md:items-center md:justify-between md:gap-10">
            <div className="max-w-2xl">
              <p className="font-['DM_Sans',sans-serif] text-[10px] font-bold uppercase tracking-[0.18em] text-indigo-300 sm:text-xs">
                Let's Talk About Your Project
              </p>

              <h3 className="mt-3 font-['Poppins',sans-serif] text-2xl font-semibold leading-tight tracking-tight text-white sm:text-3xl md:text-4xl">
                Have a question that's not listed?
              </h3>

              <p className="mt-3 max-w-xl font-['DM_Sans',sans-serif] text-sm leading-7 text-slate-300 sm:text-base sm:leading-8">
                Connect with Cube4Spaces & Paint Studio to discuss your
                requirements and explore the right approach for your space.
              </p>
            </div>

            <button
              type="button"
              onClick={handleContact}
              className="inline-flex min-h-12 w-full shrink-0 items-center justify-center gap-2.5 rounded-xl bg-white px-6 py-3.5 font-['DM_Sans',sans-serif] text-sm font-bold text-slate-950 transition-all duration-300 hover:bg-indigo-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 sm:w-auto sm:rounded-full sm:px-7"
            >
              Contact Our Team
              <ArrowDownRight className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}